import React, { useRef, useState, useCallback } from 'react';
import ImageResizerService from '../../../../../components/services/image-resizer';
import { Button } from 'ui-components/components/button';
import { InlineErrorMessage } from 'ui-components/components/inline-error-message';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { noop } from 'ui-components/default-props/noop';

import { emptyArray } from 'ui-components/default-props/empty';
import { updateStoreLogo, deleteStoreLogo, getStore } from '../../../api/store-api';
import StoreLogoImage from './store-logo-image';
import LogoUploadErrorMessage from './logo-error-message';
import messages from '../messages';

import storeStyle from '../style.module.less';

const ALLOWED_FILE_TYPES = ['image/apng', 'image/avif', 'image/gif', 'image/jpeg', 'image/png', 'image/svg+xml', 'image/webp'];

const BrandingImageInput = ({ storeGuid = '', logoUri = '', onChange = noop }) => {
  const fileUploadRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedImg, setSelectedImg] = useState(logoUri);
  const [imageErrorOrWarning, setImageErrorOrWarning] = useState(emptyArray);
  const [errorType, setErrorType] = useState('error');

  const onFileUploadClick = useCallback(() => {
    fileUploadRef.current.click();
  }, [fileUploadRef]);

  const handleFileUpload = useCallback(
    async (e) => {
      setImageErrorOrWarning(emptyArray);
      setErrorType('error');
      const uploadedFile = e.target.files[0];
      if (uploadedFile) {
        if (!ALLOWED_FILE_TYPES.includes(uploadedFile.type)) {
          setErrorType('warning');
          setImageErrorOrWarning([{ Message: 'Unsupported file type' }]);
        }
        if (ALLOWED_FILE_TYPES.includes(uploadedFile.type)) {
          try {
            const storeLogo = await updateStoreLogo(storeGuid, uploadedFile);
            await getStore(storeGuid);

            setSelectedImg(storeLogo);
            setSelectedFile(uploadedFile);
            onChange(storeLogo);
          } catch (error) {
            setErrorType('error');
            setImageErrorOrWarning(error?.response?.data?.Errors ?? [{}]);
          }
        }
      }
    },
    [setSelectedFile, storeGuid, onChange],
  );

  const handleRemoveLogo = useCallback(async () => {
    try {
      await deleteStoreLogo(storeGuid);
      await getStore(storeGuid);
      setSelectedImg();
      setSelectedFile(null);
      onChange(null);
    } catch (error) {
      setErrorType('error');
      setImageErrorOrWarning(error?.response?.data?.Errors ?? [{}]);
    }
  }, [storeGuid, onChange]);

  if (selectedFile || selectedImg) {
    return (
      <div className={storeStyle['image-wrapper']}>
        <div className={storeStyle['image-element']}>
          <ImageResizerService maxSize='300' url={selectedImg ?? logoUri}>
            {({ resizedUrl }) => <StoreLogoImage imageUrl={resizedUrl} />}
          </ImageResizerService>
          <Button className={storeStyle['remove-button']} onClick={handleRemoveLogo} theme='link'>
            <FormattedMessage {...messages.remove} />
          </Button>
        </div>
      </div>
    );
  }

  const hasError = imageErrorOrWarning.length && errorType === 'error';
  const hasWarning = imageErrorOrWarning.length && errorType === 'warning';

  return (
    <>
      <div
        className={classnames(storeStyle['store-logo-drop'], { [storeStyle.error]: hasError }, { [storeStyle.warning]: hasWarning })}
        onClick={onFileUploadClick}
      >
        <div className={storeStyle['upload-a-file']}>
          <FormattedMessage {...messages.selectFile} />
        </div>
        <div className={storeStyle['helper-sentence']}>
          <FormattedMessage {...messages.helperSentence} />
        </div>
        <input ref={fileUploadRef} className={storeStyle['hidden-file-input']} onChange={handleFileUpload} type='file' />
      </div>
      {imageErrorOrWarning.length > 0 && (
        <InlineErrorMessage className={storeStyle['logo-error']} state={errorType}>
          {imageErrorOrWarning.map((error, idx) => (
            <LogoUploadErrorMessage key={idx} error={error} />
          ))}
        </InlineErrorMessage>
      )}
    </>
  );
};

BrandingImageInput.propTypes = {
  logoUri: PropTypes.string,
  storeGuid: PropTypes.string,
  onChange: PropTypes.func,
};

export default BrandingImageInput;
